<template lang="html">
  <div
    v-if="hasBeenLoaded"
    :id="`deliverableCard${deliverable.id}`"
    v-observe-visibility="{
      callback: visibilityChanged,
      throttle: 300,
      intersection: {
        rootMargin: '1000px',
      },
    }"
    class="ui deliverable card fluid"
    :class="[`${deliverable.inferred_type}-card`, { fluid }]"
  >
    <!-- <a @click="loadData">Reload</a> -->
    <template v-if="!isVisible">
      <div class="content">
        <div class="ui placeholder">
          <div class="image header">
            <div class="line" />
            <div class="line" />
          </div>
        </div>
      </div>
      <div class="image">
        <div class="ui placeholder">
          <div class="square image" />
        </div>
      </div>
      <div class="extra content">
        <div class="ui placeholder">
          <div class="short line" />
        </div>
      </div>
    </template>

    <template v-if="isVisible">
      <div
        v-if="!editable || showCardHeader"
        class="content center aligned card-header"
      >
        <span class="icon-head">
          <i v-if="incomplete" class="ui icon wait" />
          <i
            v-if="!incomplete && !favicon"
            class="icon colored"
            :class="inferredTypeAsIconClass(deliverable.inferred_type)"
          />
          <img
            v-if="!incomplete && favicon"
            :src="favicon"
            height="55"
            width="55"
          />
        </span>
        <div class="header ui tiny statistics">
          <div
            v-if="visibleStatisticsValues.length > 0"
            class="statistic"
          >
            <div v-if="headerStat" class="value">
              <span>
                <i
                  class="ui icon dollar fitted"
                  v-if="headerStat.type == 'revenue' || headerStat.type == 'ad_spend'"
                ></i>
                {{ headerStat.total.value | abbreviateNumber }}
              </span>
            </div>
            <div
              v-if="headerStat && headerStat.details.length > 0"
              class="ui tiny popup desktop-only"
            >
              <div class="ui list">
                <div v-for="detail in headerStat.details" class="item">
                  {{ detail }}
                </div>
              </div>
            </div>
            <div
              v-if="headerStat.total && headerStat.total.value > 0"
              class="label"
            >
              {{ headerStat.total.label | capitalize }}
            </div>
          </div>
          <div v-else class="statistic">
            <div class="value">&nbsp;</div>
            <div class="label">&nbsp;</div>
          </div>
        </div>
      </div>

      <div
        v-if="shouldShowSecondaryStats || hasDemographics"
        class="ui content statistics-head"
      >
        <!-- Details Popup -->
        <div ref="statisticsDetails" class="ui wide popup desktop-only">
          <div
            v-if="isEmailType && deliverable.provider_published_at"
            class="ui small grey text send-date-popover margin-bottom-small"
          >
            <i class="icon calendar outline" />
            Sent {{ deliverable.provider_published_at | formatDate }}
          </div>

          <demographics-card
            v-if="hasDemographics"
            compact
            :demographics="deliverable.demographicStatistics"
            class="margin-bottom-small"
          />

          <div
            v-for="group in statisticsDetails"
            v-if="group.details.length > 0"
            class="ui statistic margin-left-zero left aligned"
          >
            <div class="label">
              <span v-if="deliverable.inferred_type === 'youtube' && group.name === 'engagements' && group.details && group.details.length > 0">
                Likes & Comments
              </span>
              <span v-else>{{ group.total.label | capitalize }}</span>
            </div>
            <div class="ui list margin-top-tiny margin-bottom-small">
              <div v-for="detail in group.details" class="ui item">
                {{ detail }}
              </div>
            </div>
          </div>

          <span class="ui small text">
            <a
              href="https://help.influencekit.com/en/articles/2138024-understanding-report-metrics"
              target="_blank"
            >
              <i class="icon question mark circle" />
              What do these mean?
            </a>
          </span>
        </div>

        <!--
          `unstackable`: three short stats are exactly the content that should
          stay side by side on a phone — they are meant to be read against each
          other, and stacked they spend ~175px saying three numbers. The shared
          phone layer stacks every grid at ≤767.98px and documents this class as
          the opt-out (_mobile_layout.scss). The native app had already reached
          the same design by hand (hotwire_native_screens.scss: "keep them side
          by side, as designed"); this is that rule moved into markup so both
          phone surfaces get it from one place. Inert at desktop — no rule
          outside the max-width: 767.98px block matches `unstackable`. (F-029)
        -->
        <div v-if="shouldShowSecondaryStats" class="ui equal width unstackable grid">
          <div
            v-for="statisticValue in nonZeroSecondaryStats.slice(0, 3)"
            class="ui column tiny statistic"
          >
            <div class="value">
              <span v-if="statisticValue.type == 'ad_spend' || statisticValue.type == 'revenue'">$</span>
              <span>{{ statisticValue.total.value | abbreviateNumber }}</span>
            </div>
            <!--             <div class="ui tiny popup">
              <div class="ui list">
                <div class="item" v-for="detail in statisticValue.details">
                  {{ detail }}
                </div>
              </div>
            </div>
 -->
            <div class="label">
              <span v-if="deliverable.inferred_type === 'youtube' && statisticValue.type === 'engagements' && statisticValue.details && statisticValue.details.length > 0">
                Likes & Comments
              </span>
              <span v-else>{{ statisticValue.total.label | capitalize }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- GA Date Range Display -->
      <div v-if="gaDateRangeText" class="ui center aligned margin-bottom-mini">
        <span class="ui small grey text">{{ gaDateRangeText }}</span>
      </div>

      <!-- Stats Source / Duplicate Badges -->
      <div
        v-if="statProvenance || statsSourceLabel || duplicateCount > 1"
        class="ui center aligned margin-bottom-mini"
      >
        <span
          v-if="duplicateCount > 1"
          class="stats-source-badge duplicate"
          :data-tooltip="`This post appears ${duplicateCount} times on this report`"
          data-position="bottom center"
          data-variation="mini"
        >
          Duplicate
        </span>
        <span
          v-if="statProvenance"
          class="stats-source-badge"
          :class="statProvenance.kind"
        >
          {{ statProvenance.label }}
        </span>
        <span
          v-else-if="statsSourceLabel"
          class="stats-source-badge"
          :class="statsSourceBadgeClass"
          :data-tooltip="statsSourceTooltip"
          :data-position="statsSourceTooltip ? 'bottom center' : undefined"
          :data-variation="statsSourceTooltip ? 'mini' : undefined"
        >
          {{ statsSourceLabel }}
        </span>
      </div>

      <!-- Top Audience Comments -->
      <div v-if="topComments.length > 0" class="top-comments">
        <div class="top-comments-label">Top comments</div>
        <ul class="top-comments-list">
          <li v-for="(comment, index) in topComments" :key="index" class="top-comment">
            “{{ comment }}”
          </li>
        </ul>
      </div>

      <div class="content card-content">
        <div
          v-if="searchStats && searchStats.total.count > 0"
          style="position: absolute; width: 75%; z-index: 20"
        >
          <div class="ui label left status corner search stat">
            <i class="icon yellow circular seo-trophy inverted" />
          </div>
          <div class="ui fluid inverted popup search">
            <div class="ui list">
              <div v-for="detail in searchStats.details" class="item">
                {{ detail }}
              </div>
            </div>
          </div>
        </div>

        <h3 v-if="!deliverable.url" class="margin-top-zero">
          {{ deliverable.name }}
        </h3>

        <div v-if="incomplete" class="center aligned content">
          <br /><br /><br />
          <i class="ui grey huge icon wait" />
          This deliverable is incomplete...
        </div>

        <div v-if="deliverable.description" v-html="deliverable.description" />

        <div
          v-if="deliverableTagsEnabled && tagList.length"
          class="deliverable-tags"
        >
          <button
            v-for="tag in tagList"
            :key="tag"
            type="button"
            class="deliverable-tag-chip"
            :title="`Filter report by ${tag}`"
            @click.stop.prevent="filterByTag(tag)"
          >
            <i class="tag icon" />
            <span>{{ tag }}</span>
          </button>
        </div>

        <carousel
          ref="carousel"
          :per-page="1"
          :pagination-enabled="false"
          :navigation-enabled="true"
          :mouse-drag="false"
          :touch-drag="false"
          navigation-next-label="<i class='icon large grey chevron right'></i>"
          navigation-prev-label="<i class='icon large grey chevron left'></i>"
        >
          <slide v-if="showIframelyEmbed">
            <div class="ui carousel content">
              <div
                v-if="!isPushNotificationType"
                class="iframely-embed centered"
              >
                <div class="iframely-responsive">
                  <a
                    v-if="
                      ['mailchimp', 'email'].includes(
                        deliverable.inferred_type
                      ) ||
                      (!['shopify', 'pinterest'].includes(
                        deliverable.inferred_type
                      ) &&
                        !isEmailType)
                    "
                    ref="iframelyLink"
                    :href="urlOrExpandedUrl"
                    :data-iframely-url="iframelyUrl"
                    :data-deliverable-id="deliverable.id"
                  />

                  <img
                    v-if="
                      deliverable.inferred_type === 'pinterest' &&
                      iframelyData &&
                      iframelyData.links &&
                      iframelyData.links.thumbnail
                    "
                    :src="iframelyData.links.thumbnail[0].href"
                    loading="lazy"
                  />

                  <turbo-frame
                    :id="`deliverable_${deliverable.id}_preview`"
                    v-if="
                      deliverable.inferred_type === 'activecampaign' ||
                      deliverable.inferred_type === 'drip' ||
                      deliverable.inferred_type === 'klaviyo' ||
                      deliverable.inferred_type === 'constantcontact' ||
                      deliverable.inferred_type === 'campaignmonitor' ||
                      deliverable.inferred_type === 'shopify' ||
                      deliverable.inferred_type === 'beehiiv'
                    "
                    :src="`/deliverables/${deliverable.id}/preview`"
                    loading="lazy"
                    class="ui 9:16 preview-frame-bg"
                  ></turbo-frame>

                  <div
                    v-if="
                      isEmailType &&
                      deliverable.inferred_type != 'activecampaign' &&
                      deliverable.inferred_type != 'klaviyo' &&
                      deliverable.inferred_type != 'constantcontact' &&
                      deliverable.inferred_type != 'campaignmonitor' &&
                      deliverable.inferred_type != 'beehiiv'
                    "
                    class="center aligned"
                  >
                    <br /><br /><br /><br />
                    <i class="icon envelope open outline massive disabled" />
                  </div>
                </div>
              </div>
            </div>

            <div
              v-if="
                isPushNotificationType && deliverable.provider_caption != null
              "
              v-html="deliverable.provider_caption"
            />
          </slide>

          <slide v-if="isInstagramStoryType">
            <div class="ui carousel content center aligned">
              <instagram-story-embed
                :deliverable="deliverable"
                style="width: 55%; margin: 0px auto"
              />
            </div>
          </slide>

          <slide
            :key="`${upload.id}-${upload.position}`"
            v-for="(upload, i) in deliverable.uploads"
            v-if="deliverable.uploads.length > 0 && !isInstagramStoryType"
          >
            <div
              v-if="
                ['mp4', 'mov'].some((ext) => upload.expiring_url.includes(ext))
              "
              :id="'embed' + deliverable.id"
              class="ui video embed active 9:16"
              style="width: 40%; margin: 0px auto; padding-bottom: 71%"
              :data-url="upload.expiring_url"
            >
              <video
                :src="upload.expiring_url"
                width="100%"
                controls
                allow="autoplay"
                preload="none"
              />
            </div>

            <div
              v-else-if="upload.expiring_url.includes('pdf') && !isCardPage"
              class="flex-wrap"
            >
              <iframe
                :src="`${upload.expiring_url}#toolbar=0&view=Fit`"
                width="100%"
                height="99%"
                frameborder="0"
              />
            </div>
            <div
              v-else-if="upload.expiring_url.includes('pdf') && isCardPage"
              class="flex-wrap"
            >
              <img
                :src="'data:image/png;base64,' + upload.pdf_thumbnail_base64"
              />
            </div>

            <div v-else class="flex-wrap">
              <a
                class="slide-image"
                :href="upload.expiring_url"
                target="_blank"
                :style="{
                  backgroundImage: `url(${upload.expiring_url})`,
                  backgroundPosition: 'center',
                  backgroundSize: 'contain',
                  backgroundRepeat: 'no-repeat',
                }"
              />
              <div
                v-if="deliverable.uploads.length > 1"
                class="center aligned content pagination-label"
              >
                <span> {{ i + 1 }}/{{ deliverable.uploads.length }} </span>
              </div>
            </div>
          </slide>
        </carousel>

        <a
          v-if="canShowMore && showMore"
          class="opener"
          @click.stop.prevent="openModal"
        >
          <i class="icon expand" />
        </a>
      </div>

      <div
        v-if="deliverable.url && !isInstagramStoryType"
        class="extra content"
      >
        <a
          v-if="showExternalLink"
          class="link"
          :href="externalLink"
          target="_blank"
        >
          <i class="icon external alternate" />

          <span v-if="deliverable.inferred_type === 'url'">View on Blog</span>
          <span v-if="deliverable.inferred_type === 'bitly'">View link</span>
          <span v-if="deliverable.inferred_type === 'short_io'">View link</span>

          <span v-if="isEmailType"
            >View <span class="provider">email</span></span
          >
          <span v-if="isPodcastType"
            >View <span class="provider">podcast</span></span
          >

          <span v-if="inferredTypeKey.includes('other')"
            >View <span class="provider">link</span></span
          >
          <span
            v-else-if="
              !['email', 'url', 'bitly', 'short_io', 'other', ''].includes(
                inferredTypeKey
              ) &&
              !isEmailType &&
              !isPodcastType
            "
            >View on
            <span class="provider">
              {{
                inferredTypeAsIconClass(deliverable.inferred_type) | toCamelCase
              }}
            </span>
          </span>
        </a>
      </div>
      <div class="ui modal" />
      <span
        v-if="editable && !showStatusLabel"
        class="ui right corner label status"
        @click.alt="openReportingStatus"
      >
        <span
          data-forcewrap
          :data-tooltip="refreshTooltip"
          data-position="bottom right"
          data-variation="wide"
          @mouseover="syncRefreshClock"
        >
          <a @click="refreshStats">
            <i :class="refreshIcon" />
          </a>
        </span>
      </span>

      <span
        v-if="showStatusLabel"
        class="ui right corner label"
        @click="refreshStats"
        @mouseover="onStatusHover"
        @click.alt="openReportingStatus"
      >
        <span
          data-forcewrap
          :data-tooltip="refreshTooltip"
          data-position="bottom right"
          data-variation="wide"
        >
          <i :class="refreshIcon" />
        </span>
      </span>
    </template>
  </div>
</template>

<script>
import IntersectionObserver from "intersection-observer";
import _ from "lodash";
import { mapGetters, mapState } from "vuex";
import store from "store";
import Vue from "vue/dist/vue.esm";
import { Carousel, Slide } from "vue-carousel";
import InstagramStoryEmbed from "views/deliverables/components/instagramStoryEmbed.vue";
import DemographicsCard from "views/deliverables/components/demographicsCard.vue";
import VueObserveVisibility from "vue-observe-visibility";
import ResizeObserver from "resize-observer-polyfill";
import Modal from "components/modal.vue";
import EventBus from "components/eventbus";
import Chartkick from "chartkick";
import VueChartkick from "vue-chartkick";
import "functions/filters";
import {
  REFRESH_STATES,
  createRefreshTracker,
  refreshIconClass,
  refreshTitle,
  relativeUpdatedLabel,
} from "lib/refreshStatsState.mjs";

Vue.use(VueObserveVisibility);
Vue.use(VueChartkick, {Chartkick});

const $ = window.jQuery;

export default {
  store,
  components: {
    InstagramStoryEmbed,
    Modal,
    Carousel,
    Slide,
    DemographicsCard,
  },
  props: {
    fluid: {
      type: Boolean,
      default: false,
    },
    initialDeliverable: Object,
    loadDataRemotely: {
      type: Boolean,
      default: false,
      description: "Load data for this card remotely",
    },
    lazyLoad: {
      type: Boolean,
      default: true,
    },
    canShowMore: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    const $this = this;
    return {
      modalCardComponent: null,
      modalCardData: null,
      isVisible: $this.lazyLoad ? false : true,
      favicon: false,
      showMore: false,
      hasBeenLoaded: !$this.loadDataRemotely,
      deliverable: { ...($this.initialDeliverable || {}) },
      iframelyData: null,
      refreshState: REFRESH_STATES.IDLE,
      // Bumped on hover so "Updated 3 hours ago" doesn't go stale on a report
      // left open all afternoon. Cheaper than an interval per card.
      refreshClock: Date.now(),
    };
  },
  computed: {
    ...mapGetters("deliverable", ["inferredTypeAsIconClass"]),
    ...mapState("report", ["accessToken"]),
    editable() {
      return this.$store.state.report.editable;
    },
    tagList() {
      return this.deliverable.tag_list || [];
    },
    // Per-tenant gate (seeded by the share view). When off, tag chips never
    // render even if the deliverable carries tags set via MCP.
    deliverableTagsEnabled() {
      return this.$store.state.report.deliverableTagsEnabled;
    },
    // The refresh control answers one question — "is this current, and if not,
    // what's happening?" While a refresh is in flight it reports the refresh;
    // at rest it falls back to the deliverable's own reporting status, which
    // the model already computes (Deliverable#reporting_status), so a failure
    // stays visible instead of the control quietly reverting to idle.
    refreshBusy() {
      return this.refreshState !== REFRESH_STATES.IDLE;
    },
    refreshIcon() {
      if (this.refreshBusy) return refreshIconClass(this.refreshState);
      if (this.showStatusLabel) {
        return this.deliverable.reporting_status
          ? `icon ${this.deliverable.reporting_status.icon}`
          : "icon question mark";
      }
      // The healthy card keeps the sync glyph: it is a button, and it should
      // still look like one.
      return "icon sync";
    },
    refreshTooltip() {
      const status = this.deliverable.reporting_status;
      return refreshTitle(this.refreshState, {
        inferredType: this.deliverable.inferred_type,
        statusMessage: status && status.message,
        statusOk: status ? status.status === "ok" : null,
        updatedLabel: this.lastUpdatedLabel,
      });
    },
    lastUpdatedLabel() {
      return relativeUpdatedLabel(
        this.deliverable.last_fetched,
        new Date(this.refreshClock)
      );
    },
    showStatusLabel() {
      if (this.accessToken) {
        return false;
      }
      return (
        (this.editable && this.deliverable.last_error_at) ||
        !this.deliverable.last_fetched ||
        (this.deliverable.reporting_status &&
          (this.deliverable.reporting_status.status === "error" ||
            this.deliverable.reporting_status.status === "unknown"))
      );
    },
    showExternalLink() {
      return (
        this.deliverable.url &&
        this.deliverable.inferred_type != "convertkit" &&
        // this.deliverable.inferred_type != "drip" &&
        //this.deliverable.inferred_type != "activecampaign" &&
        this.deliverable.inferred_type != "madmimi" &&
        this.deliverable.inferred_type != "klaviyo" &&
        this.deliverable.inferred_type != "shopify" &&
        this.deliverable.inferred_type != "expertsender" &&
        !this.isPushNotificationType
      );
    },
    showCardHeader() {
      return true;
    },
    // Stats the creator has chosen to hide (per-stat show/hide toggle in the
    // editor). Display-only: the underlying data is untouched, we just drop the
    // hidden metric_types here so the card — and any report rendering it —
    // omits them. Everything below renders from this filtered list.
    visibleStatisticsValues() {
      const values = this.deliverable.statisticsValues || [];
      const hidden = this.deliverable.hidden_stats || [];
      if (!hidden.length) return values;
      return values.filter((stat) => !hidden.includes(stat.type));
    },
    headerStat() {
      if (!this.visibleStatisticsValues) {
        return false;
      }
      const index = this.visibleStatisticsValues.findIndex(
        (value) => value.total.value > 0
      );

      if (index !== -1) {
        return this.visibleStatisticsValues[index];
      }
      return false;
    },
    secondaryStats() {
      if (!this.visibleStatisticsValues) {
        return false;
      }
      return _.reject(
        _.difference(this.visibleStatisticsValues, [this.headerStat]),
        (stat) =>
          stat.type === "search_position" || stat.type === "positive_comments"
      );
    },
    nonZeroSecondaryStats() {
      if (!this.secondaryStats) return [];
      return this.secondaryStats.filter(
        (stat) => stat.total.value != 0 && stat.total.value != "" && stat.total.value != "00:00:00"
      );
    },
    shouldShowSecondaryStats() {
      return this.nonZeroSecondaryStats.length > 0;
    },
    hasDemographics() {
      return (this.deliverable.demographicStatistics || []).length > 0;
    },
    searchStats() {
      if (!this.visibleStatisticsValues) {
        return false;
      }
      return _.find(this.visibleStatisticsValues, {
        type: "search_position",
      });
    },
    statisticsDetails() {
      return this.visibleStatisticsValues
        .map((s) => {
          return { name: s.type, details: s.details, total: s.total };
        })
        .flat();
    },
    incomplete() {
      return (
        !this.deliverable.url &&
        !this.deliverable.description &&
        this.deliverable.statisticsValues &&
        this.deliverable.statisticsValues.length < 1
      );
    },
    // How many cards on this report show this card's post (2+ = a duplicate).
    // The server marks the candidates once per load (report/duplicatePosts,
    // editors only); counted here over the cards actually loaded, and by link
    // when "Remove duplicates" already folds identical links into one card.
    duplicateCount() {
      const posts = this.$store.state.report.duplicatePosts || {};
      const post = posts[this.deliverable.id];
      if (!post) return 0;
      const twins = _.filter(
        _.values(this.$store.state.report.deliverables),
        (d) => posts[d.id] === post
      );
      const report = this.$store.state.report.report;
      if (report && report.sections && report.sections.removeDuplicates) {
        return _.uniqBy(twins, "url").length;
      }
      return twins.length;
    },
    statProvenance() {
      return this.deliverable.stat_provenance || null;
    },
    statsSourceLabel() {
      const source = this.deliverable.stats_source;
      if (source === 'estimated') return 'Estimated';
      return null;
    },
    statsSourceBadgeClass() {
      return this.deliverable.stats_source === 'estimated' ? 'estimated' : 'public-api';
    },
    statsSourceTooltip() {
      if (this.deliverable.stats_source === 'estimated') {
        return "Stats estimated based on this creator's typical performance. Connect their account for verified data.";
      }
      return null;
    },
    topComments() {
      const hidden = this.deliverable.hidden_stats || [];
      if (hidden.includes("positive_comments")) return [];
      return (this.deliverable.top_comments || []).slice(0, 3);
    },
    gaDateRangeText() {
      if (!this.deliverable.ga_settings) return null;
      const { ga_start_date, ga_end_date } = this.deliverable.ga_settings;

      if (!ga_start_date && !ga_end_date) return null;

      const formatDate = (dateStr) => {
        if (!dateStr) return null;
        const date = new Date(dateStr);
        // Format as M/D/YY
        return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear().toString().substr(-2)}`;
      };

      const start = formatDate(ga_start_date);
      const end = formatDate(ga_end_date);

      if (start && end) {
        return `From ${start} to ${end}`;
      } else if (start) {
        return `From ${start} onwards`;
      } else if (end) {
        return `Through ${end}`;
      }
    },
    showIframelyEmbed() {
      return (
        this.deliverable.url &&
        !this.deliverable.hide_embed &&
        !this.isInstagramStoryType &&
        !this.hideEmailIcon
      );
    },
    isCardPage() {
      return window.location.pathname.includes("/card");
    },
    // Iframely's iframe-API URL. The in-house loader (ik_embed.js) ignores
    // this attribute and renders from href; Iframely's embed.js REQUIRES it.
    // Keeping it populated is what makes the URL_METADATA_PROVIDER=iframely
    // hot swap work on deliverable cards with no mode-awareness here.
    iframelyUrl() {
      let url = `https://cdn.iframe.ly/api/iframe?&url=${encodeURIComponent(
        this.urlOrExpandedUrl
      )}&key=691c90188e4da53cfe4eaf8b181bcb57&autoplay=0`;
      if (!window.location.search.includes("eagerLoadEmbeds")) {
        url = `${url}&lazy=1`;
      }
      return url;
    },
    iframelyApi() {
      return `/embed/metadata?url=${encodeURIComponent(this.urlOrExpandedUrl)}`;
    },
    externalLink() {
      switch (this.deliverable.inferred_type) {
        case "activecampaign":
        case "drip":
          return `/deliverables/${this.deliverable.id}/preview`;
        case "beehiiv":
          // Use the web_url from Beehiiv API if available, otherwise fall back to the original URL
          return this.deliverable.beehiiv_web_url || this.urlOrExpandedUrl;
        default:
          return this.urlOrExpandedUrl;
      }
    },
    urlOrExpandedUrl() {
      return this.deliverable.url;
    },
    hostFromUrl() {
      const host = new URL(this.deliverable.url).host.replace("www.", "");
      return host;
    },
    isPodcastType() {
      return ["art19", "omny_studio", "transistor"].includes(
        this.deliverable.inferred_type
      );
    },
    isEmailType() {
      return [
        "email",
        "mailerlite",
        "mailchimp",
        "aweber",
        "convertkit",
        "beehiiv",
        "drip",
        "campaignmonitor",
        "constantcontact",
        "sendfox",
        "madmimi",
        "activecampaign",
        "klaviyo",
        "expertsender",
        "flodesk",
      ].includes(this.deliverable.inferred_type);
    },
    // inferred_type is legitimately empty while a URL edit is in flight (the
    // url watcher in ../form.vue clears it and waits for the server to
    // re-infer). Calling .toLowerCase() on that directly in the template threw
    // during render, which aborts Vue's patch and freezes the card.
    inferredTypeKey() {
      return (this.deliverable.inferred_type || "").toLowerCase();
    },
    isPushNotificationType() {
      return ["one_signal", "push_engage"].includes(
        this.deliverable.inferred_type
      );
    },
    isInstagramStoryType() {
      return ["instagram_stories"].includes(this.deliverable.inferred_type);
    },
    hideEmailIcon() {
      return this.isEmailType && this.hasDescriptionOrCaptionOrUploads;
    },
    hasDescriptionOrCaptionOrUploads() {
      return (
        !this.inferred_type === "mailchimp" &&
        (this.deliverable.provider_caption ||
          (this.deliverable.description &&
            this.deliverable.description != "") ||
          (this.deliverable.uploads && this.deliverable.uploads.length > 0))
      );
    },
  },
  watch: {
    initialDeliverable: {
      deep: true,
      handler(val) {
        let clone = { ...val }; // val is a reference to the prop,
        delete clone.statisticsValues; // don't mutate it here, or else we have weird side effects
        this.deliverable = { ...this.deliverable, ...clone };
      },
    },
    isVisible(val, oldVal) {
      if (val && val != oldVal) {
        this.$nextTick(() => {
          this.initCard();
        });
      }
    },
    showIframelyEmbed(val, oldVal) {
      if (val && val != oldVal) {
        this.$nextTick(() => {
          this.loadIframelyEmbed();
        });
      }
    },
  },
  created() {
    // Not in data(): it owns timers and has no business being reactive. The
    // reactive part is refreshState, which it pushes out through onChange.
    this.refreshTracker = createRefreshTracker({
      onChange: (state) => {
        this.refreshState = state;
      },
      onTimeout: this.recheckRefresh,
    });
  },
  mounted() {
    const $this = this;

    $this.$nextTick(() => {
      if ($this.loadDataRemotely) {
        $this.loadData();
      } else {
        $this.emitLoaded();
      }

      if (!this.lazyLoad) {
        $this.initCard();

        if (window.location.search.includes("eagerLoadEmbeds")) {
          $this.visibilityChanged(true);
        }
      }
    });
  },
  beforeDestroy() {
    this.refreshTracker.destroy();
  },
  updated() {
    const $this = this;
    this.$nextTick(() => {
      $this.initPopups();
    });
  },
  methods: {
    visibilityChanged(isVisible) {
      this.isVisible = this.isVisible || isVisible;
    },
    filterByTag(tag) {
      // Bubble the click up to the report view, which owns the tag filter state.
      EventBus.$emit("filterByDeliverableTag", tag);
    },
    getIframelyResponse() {
      if (
        !this.iframelyData &&
        (this.deliverable.inferred_type === "url" ||
          this.deliverable.inferred_type === "pinterest" ||
          this.deliverable.inferred_type === "other")
      ) {
        $.get(this.iframelyApi).done((data) => {
          this.iframelyData = data;
          if (
            this.deliverable.inferred_type === "url" &&
            data.links &&
            data.links.icon
          ) {
            this.favicon = data.links.icon[0].href;
          }
          if (this.deliverable.inferred_type === "pinterest") {
            $(this.$el)
              .find(".iframely-responsive")
              .removeClass("iframely-responsive");
          }

          this.$nextTick(() => {
            this.setShowMore();
          });
        });
      }
    },
    openModal() {
      this.$emit("openModal", this.deliverable);
    },
    loadReportingStatus() {
      if (!this.deliverable.reporting_status) {
        $.getJSON(`/deliverables/${this.deliverable.id}/status`).done(
          (data) => {
            this.$set(
              this.deliverable,
              "reporting_status",
              data.reporting_status
            );
          }
        );
      }
    },
    openReportingStatus() {
      window.open(`/deliverables/${this.deliverable.id}/status`, "_blank");
    },
    setShowMore() {
      if (this.$el) {
        const cardContent = $(this.$el).find(".card-content")[0];
        this.showMore =
          cardContent && cardContent.scrollHeight > cardContent.clientHeight;
      }
    },
    observeEmbedResize() {
      const ro = new ResizeObserver(this.setShowMore);

      const embedEl = $(this.$el).find(
        ".embed-wrapper, .iframely-responsive, .iframely-embed, .card-content img"
      )[0];
      if (embedEl) {
        ro.observe(embedEl);
      }
    },
    loadData() {
      return $.read(`/deliverables/${this.deliverable.id}.json`)
        .done((data) => {
          this.hasBeenLoaded = true;
          Object.assign(this.deliverable, data);
          // this.$set(this.deliverable, "statisticsValues", data.statisticsValues);
        })
        .then(() => {
          this.$forceUpdate();
          this.emitLoaded();
        });
    },
    refreshStats() {
      this.syncRefreshClock();
      this.refreshTracker.start();

      $.read(`/deliverables/${this.deliverable.id}/refresh`)
        .done((data) => this.refreshTracker.enqueued(data && data.status))
        .fail(() => this.refreshTracker.failed());
    },
    // The spinner window elapsed with no broadcast. Don't guess between "still
    // queued" and "it finished while this tab was asleep" — go and look. If the
    // stats moved we show the confirmation; if they didn't, the control settles
    // on "waiting", which names the rate limit rather than inventing an ETA.
    recheckRefresh() {
      const before = this.deliverable.last_fetched;
      const request = this.loadData();
      if (!request || !request.then) return;

      request.then(() => {
        this.syncRefreshClock();
        if (this.deliverable.last_fetched !== before) {
          this.refreshTracker.completed();
        }
      });
    },
    syncRefreshClock() {
      this.refreshClock = Date.now();
    },
    onStatusHover() {
      this.syncRefreshClock();
      this.loadReportingStatus();
    },
    emitLoaded() {
      this.$emit("cardLoaded", this.deliverable);
    },
    initPopups() {
      $(this.$el)
        .find(".card-header,.statistics-head")
        .popup({
          closable: false,
          observeChanges: true,
          popup: $(this.$refs.statisticsDetails),
          hoverable: true,
          position: "right center",
          forcePosition: "right center",
          lastResort: "right center",
          movePopup: true,
          target: $(this.$el).find(".content.statistics-head"),
          transition: "fade",
          delay: {
            show: 250,
            hide: 50,
          },
        });

      // $(this.$el)
      //   .find(".statistic .value")
      //   .popup({
      //     observeChanges: false,
      //     inline: true,
      //     hoverable: true,
      //     position: "top center",
      //     boundary: this.$el,
      //     context: this.$el,
      //     movePopup: false
      //   });

      $(this.$el)
        .find(".search.stat")
        .popup({
          observeChanges: false,
          inline: true,
          hoverable: true,
          position: "bottom left",
          boundary: $(this.$el).find(".card-content"),
          lastResort: true,
          offset: 8,
        });
    },
    initEmbeds() {
      $(this.$el)
        .find(".video.embed")
        .embed({
          icon: "video play",
          onCreate(url) {
            if (url.includes(".mov")) {
              $(this).find("iframe").remove();
            }
          },
        });
      $(this.$el)
        .find(".iframely-embed .ui.embed")
        .embed({
          autoplay: null,
          onDisplay() {
            const iframe = $(this).find("iframe")[0];
            $(this).addClass("scrolling");
            $(iframe).on("load", function () {
              const height = `${$(this).contents().height()}px`;
              $(iframe).height(height);
            });
          },
        });
    },
    initHeartbeat() {
      if (typeof deliverablesChannel !== "undefined") {
        const $this = this;
        window.deliverablesChannel(
          $this.deliverable.id,
          "deliverable-updated",
          function () {
            $this.loadData();
            $this.syncRefreshClock();
            $this.refreshTracker.completed();
          }
        );

        window.deliverablesChannel(
          $this.deliverable.id,
          "deliverable-refresh-queued",
          function (data) {
            $this.deliverable.reporting_status =
              data.deliverable.reporting_status;
          }
        );
      }

      // if (typeof hb !== "undefined") {
      //   const channel = hb.pusher.subscribe(
      //     `deliverable-${this.deliverable.id}`
      //   );

      //   channel.bind("deliverable-refresh-queued", (data) => {
      //     this.deliverable.reporting_status = data.reporting_status;
      //   });
      //   channel.bind("deliverable-updated", () => {
      //     this.loadData();
      //     this.refreshingStats = false;
      //   });
      // }
    },
    loadIframelyEmbed() {
      if (this.$refs.iframelyLink) {
        if (typeof window.iframely !== "undefined") {
          try {
            window.iframely.load(this.$refs.iframelyLink);
          } catch (error) {
            console.log(error);
          }
        }
      }
    },
    initCard() {
      this.initPopups();
      this.initEmbeds();
      this.initHeartbeat();
      this.loadIframelyEmbed();
      this.getIframelyResponse();

      if (this.canShowMore) {
        this.setShowMore();
        this.observeEmbedResize();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~vue-resize/dist/vue-resize.css";

[data-tooltip][data-forcewrap]:after {
  white-space: normal;
  z-index: 22;
}

div.ui[class*="9:16"].embed {
  padding-bottom: 177%;
}

div.ui.embed.scrolling {
  overflow: auto !important;
}

.extra.content {
  word-wrap: break-word;

  a.link span span.provider {
    text-transform: capitalize;
  }
}

.deliverable-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
  margin-top: 0.65rem;
}

.deliverable-tag-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  margin: 0;
  padding: 0.32em 0.7em;
  border: 1px solid rgba(244, 128, 111, 0.25);
  border-radius: 999px;
  background: rgba(244, 128, 111, 0.08);
  color: #c85a46;
  font-size: 0.72rem;
  font-weight: 600;
  line-height: 1;
  cursor: pointer;
  transition: all 0.15s ease;

  .icon {
    margin: 0;
    font-size: 0.82em;
    opacity: 0.75;
  }

  &:hover {
    background: rgba(244, 128, 111, 0.16);
    border-color: rgba(244, 128, 111, 0.5);
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px rgba(244, 128, 111, 0.3);
  }
}

.ui.right.corner.label {
  z-index: 20;
}

.preview-frame-bg {
  background: var(--bg-surface);
}

.status.label {
  border-color: transparent;
  a {
    i.icon {
    }
    color: var(--text-secondary);
  }

  &.search.stat {
    top: -9px;
    left: 2px;
    i {
      top: 0;
      left: 0;
      font-size: 20px;
      box-shadow: 0 8px 16px -4px rgba(0, 0, 0, 0.32) !important;
    }
  }
}

</style>

<style lang="scss">
.ui.deliverable.card .card-content {
  display: flex;
  flex-direction: column;

  .carousel {
    height: 100%;

    .ui.image {
      height: 100%;

      img {
        max-height: 100%;
      }
    }
  }

  .VueCarousel {
    height: 100%;
  }

  .VueCarousel-wrapper,
  .VueCarousel-inner {
    height: 100% !important;
  }

  .VueCarousel-navigation {
    position: absolute;
    bottom: 45px;
    width: 100%;
  }

  .VueCarousel-navigation-prev {
    /* transform: translateY(-80%) translateX(400%) !important; */
    top: 0 !important;
    left: 35% !important;
    transform: none !important;
  }
  .VueCarousel-navigation-next {
    top: 0 !important;
    right: 35% !important;
    transform: none !important;
    /* transform: translateY(-80%) translateX(-400%) !important; */
  }

  .VueCarousel-slide {
    .flex-wrap {
      height: 100%;
      display: flex;
      flex-direction: column;

      .slide-image {
        display: block;
        flex: 1 1 auto;
      }
      .pagination-label {
        flex: 0 0 55px;
      }
    }
  }

  .VueCarousel-slide {
    overflow: hidden;
  }
  .VueCarousel-slide .content {
    display: block;
  }
}

.ui.deliverable.card.youtube-card .content.card-header {
  background: linear-gradient(180deg, #ff0000 8px, var(--bg-surface) 8px);
}
.ui.deliverable.card.facebook-card .content.card-header {
  background: linear-gradient(180deg, #476eb1 8px, var(--bg-surface) 8px);
}
.ui.deliverable.card.pinterest-card .content.card-header {
  background: linear-gradient(180deg, #c0001d 8px, var(--bg-surface) 8px);
}
.ui.deliverable.card.rss-card .content.card-header,
.ui.deliverable.card.url-card .content.card-header,
.ui.deliverable.card.google-card .content.card-header {
  background: linear-gradient(180deg, lighten(#f4806f, 5%) 8px, var(--bg-surface) 8px);
}
.ui.deliverable.card.twitter-card .content.card-header {
  background: linear-gradient(180deg, #009dea 8px, var(--bg-surface) 8px);
}

.ui.deliverable.card.mailchimp-card {
  .content.card-header {
    background: linear-gradient(#239ab9 8px, var(--bg-surface) 8px);
  }
}

.ui.deliverable.card.mailerlite-card {
  .content.card-header {
    background: linear-gradient(#3eaf7c 8px, var(--bg-surface) 8px);
  }
}

.ui.deliverable.card.aweber-card {
  .content.card-header {
    background: linear-gradient(180deg, #2072b9 8px, var(--bg-surface) 8px);
  }
}

.ui.deliverable.card.bitly-card {
  .content.card-header {
    background: linear-gradient(180deg, rgba(97, 179, 222, 0.9) 8px, var(--bg-surface) 8px);
  }
}

.ui.deliverable.card.short_io-card {
  .content.card-header {
    background: linear-gradient(180deg, #55af86 8px, var(--bg-surface) 8px);
  }
}

.ui.deliverable.card.campaignmonitor-card {
  .content.card-header {
    background: linear-gradient(#7856ff 8px, var(--bg-surface) 8px);
  }
}

.ui.deliverable.card.convertkit-card {
  .content.card-header {
    background: linear-gradient(#fb6970 8px, var(--bg-surface) 8px);
  }
}

.ui.deliverable.card.beehiiv-card {
  .content.card-header {
    background: linear-gradient(#060419 8px, var(--bg-surface) 8px);
  }
}
.ui.deliverable.card.constantcontact-card {
  .content.card-header {
    background: linear-gradient(#1856ed 8px, var(--bg-surface) 8px);
  }
}
.ui.deliverable.card.drip-card {
  .content.card-header {
    background: linear-gradient(#f224f2 8px, var(--bg-surface) 8px);
  }
}

.ui.deliverable.card.expertsender-card {
  .content.card-header {
    background: linear-gradient(#552d7e 8px, var(--bg-surface) 8px);
  }
}

.ui.deliverable.card.klaviyo-card {
  .content.card-header {
    background: linear-gradient(#24ad69 8px, var(--bg-surface) 8px);
  }
}

.ui.deliverable.card.sendfox-card {
  .content.card-header {
    background: linear-gradient(#fd6655 8px, var(--bg-surface) 8px);
  }
}

.ui.deliverable.card.tik_tok-card {
  .content.card-header {
    background: linear-gradient(#ee1d52 8px, var(--bg-surface) 8px);
  }
}

.ui.deliverable.card.twitch-card {
  .content.card-header {
    background: linear-gradient(#9146ff 8px, var(--bg-surface) 8px);
  }
}

.ui.deliverable.card.shopify-card {
  .content.card-header {
    background: linear-gradient(#008060 8px, var(--bg-surface) 8px);
  }
}

.ui.deliverable.card.activecampaign-card {
  .content.card-header {
    background: linear-gradient(rgba(57, 110, 226, 1) 8px, var(--bg-surface) 8px);
  }
}

.ui.deliverable.card.linkedin-card {
  .content.card-header {
    background: linear-gradient(#0976b4 8px, var(--bg-surface) 8px);
  }
}

.ui.deliverable.card.madmimi-card {
  .content.card-header {
    background: linear-gradient(#86c3ea 8px, var(--bg-surface) 8px);
  }
}

.ui.deliverable.card.instagram_stories-card {
  .content.card-header {
    background: linear-gradient(#ac279f 8px, var(--bg-surface) 8px);
  }
}

.ui.deliverable.card.threads-card {
  .content.card-header {
    background: linear-gradient(#3f3f3f 8px, var(--bg-surface) 8px);
  }
}

.ui.deliverable.card.one_signal-card,
.ui.deliverable.card.push_engage-card {
  .content.card-header {
    background: linear-gradient(#e24b4d 8px, var(--bg-surface) 8px);
  }
}

.ui.deliverable.card.omny_studio-card {
  .content.card-header {
    background: linear-gradient(#6313bc 8px, var(--bg-surface) 8px);
  }
}

.ui.deliverable.card.art19-card {
  .content.card-header {
    background: linear-gradient(#1373b6 8px, var(--bg-surface) 8px);
  }
}

.ui.ui.deliverable.card.transistor-card {
  .content.card-header {
    background: linear-gradient(#0c1824, 8px, var(--bg-surface) 8px);
  }
}

.ui.deliverable.card.instagram-card {
  .content.card-header {
    background: linear-gradient(#ac279f 8px, var(--bg-surface) 8px);
  }
}

@media (min-width: 1200px) {
  .ui.deliverable.card > .content.card-header .ui.statistic > .value,
  .ui.cards > .card > .content.card-header .ui.statistic > .value,
  .ui.deliverable.card
    > .content.card-header
    .ui.statistics
    .statistic
    > .value,
  .ui.cards > .card > .content.card-header .ui.statistics .statistic > .value {
    font-size: 2.29rem !important;
  }
}

.ui.card.deliverable {
  box-shadow: 0px 24px 32px 8px rgba(0, 0, 0, 0.04);

  > .ui.right.corner.label {
    margin-top: 8px;
  }

  .card-header .statistics {
    margin-top: 1.5rem;
  }
  .header .statistic {
    max-width: 56% !important;
    .value {
      color: var(--text-primary);
      font-weight: bold;
      line-height: 40px;
    }
    .label {
      color: var(--text-secondary);
      font-size: 12px;
      letter-spacing: 0.6px;
      line-height: 12px;
    }
  }

  .statistics-head {
    flex-grow: 0 !important;
  }

  .content {
    overflow: hidden;
  }

  > .content.card-header {
    &.disconnected {
      height: 100px;

      .icon.cloud-slash {
        font-size: 25px;
        margin-top: 0.5em;
        margin-bottom: 0.5em;
        opacity: 0.75;
      }

      .header {
        font-weight: 600;
      }
    }
    .icon-head img {
      width: 45px !important;
      height: 45px !important;
    }
  }
  > .content.card-header {
    position: relative;
    padding: 1px 25px;
    color: var(--text-primary);
    background: linear-gradient(var(--color-accent-gold) 8px, var(--bg-surface) 8px);
    flex-grow: 0;
    min-height: 100px;
    overflow: visible;

    .header {
      color: var(--text-primary);
    }

    .icon-head {
      -webkit-transform: translate(0, -50%);
      transform: translate(0, -50%);
      color: var(--color-accent-gold);
      top: 50%;
      left: 25px;
      position: absolute;
      .icon {
        font-size: 3rem;
        line-height: 1;
      }
    }
  }

  > .content:after {
    display: none !important;
  }

  .ui.statistics {
    -ms-flex-pack: distribute;
    justify-content: space-around;
  }

  .ui.statistics-head {
    padding-top: 1.292em;
    padding-bottom: 1.292em;
    overflow: visible;
    // F-029 — a floor, not a ceiling. This box carried `height: 80px`, which
    // only ever fit because the stat columns happened to lay out horizontally;
    // the box never measured its children, so any layout that made them taller
    // overflowed it silently (`overflow: visible` above means the excess paints
    // through the caption below rather than clipping or scrolling).
    //
    // It was wrong at EVERY width, not just phone: at 1280 the grid measured
    // 84.4px against the 80px pin — desktop had been overflowing by 4.4px all
    // along and simply hadn't struck text yet. At 375 the shared phone layer
    // stacks the grid and the overflow reached 95.2px, which is where it became
    // three visible text-on-text collisions on the shared report.
    //
    // `min-height` keeps the 80px the design wants when the stats are short and
    // lets the box grow when they aren't, at every width. Desktop cards grow
    // ~8.4px as a result — a deliberate, approved break from the
    // desktop-byte-identical invariant, because the pin was a bug there too.
    min-height: 80px;

    .ui.wide.popup {
      width: 205px !important;
      top: 100px !important;
      &:before {
        top: 32px !important;
      }
      .statistic {
        width: 100%;
        display: block;
      }
    }
  }

  &:hover {
    .ui.statistics-head .statistic {
      .value {
        cursor: pointer;
        text-decoration: underline;
        text-decoration-style: dashed;
        text-underline-position: under;
        text-decoration-color: var(--text-secondary);
      }
    }
  }

  .ui.statistics-head .statistic {
    margin-bottom: 0 !important;
    margin-left: 0 !important;
    padding-right: 0.5em !important;
    padding-left: 0.5em !important;

    > .value {
      color: var(--text-primary) !important;
      font-weight: 600 !important;
      font-size: 18px !important;
      white-space: nowrap;
      line-height: 1.8em;
    }
    .label {
      color: var(--text-secondary) !important;
      font-size: 10px;
      line-height: 1.2em;
      font-weight: 400;
    }
  }

  .card-content {
    position: relative;
    overflow: hidden;
    height: 388px;
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    padding-bottom: 0;
  }

  .card-content > img {
    max-width: 100%;
    height: auto;
  }

  .extra {
    text-align: center;
    position: relative !important;

    .link {
      font-weight: 600;
      font-size: 1rem;
      color: var(--color-primary) !important;
      letter-spacing: 0;
      &:hover {
        opacity: 0.8;
      }
      .icon {
        margin-right: 8px;
      }
    }

    .link-icon {
      color: var(--color-primary) !important;
      -webkit-transform: translate(0, -50%);
      transform: translate(0, -50%);
      top: 50%;
      right: 40px;
      position: absolute;
      font-size: 1.8rem;
      line-height: 1em;

      &:hover {
        opacity: 0.7;
      }
    }
  }

  .opener {
    display: block;
    background: linear-gradient(
      to bottom,
      rgba(0, 0, 0, 0),
      rgba(0, 0, 0, 0.4)
    );
    height: 48px;
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    transition: all 0.3s linear !important;
    :hover {
      background: rgba(0, 0, 0, 0.4);
    }

    .icon {
      color: #ffffff;
      -webkit-transform: translate(-50%, -50%);
      transform: translate(-50%, -50%);
      top: 50%;
      right: 0;
      position: absolute;
      font-size: 2rem;
      line-height: 1em;
    }
  }

  .content.center.aligned.pagination-label {
    color: var(--text-muted);
    font-weight: 600;
    flex-grow: 0;
    flex-shrink: 0;
    height: 60px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}

// .ui.deliverable.card .card-content {
//   height: 100%;
//   max-height: 600px;
// }

@media (min-width: 768px) {
  .ui.deliverable.card .card-content {
    height: 338px;
  }
}
@media (min-width: 1200px) {
  .ui.deliverable.card .card-content {
    height: 338px;
  }
}

@media (min-width: 768px) {
  .ui.deliverable.card .extra {
    padding: 1.5rem !important;
  }
}
@media (min-width: 768px) {
  .ui.deliverable.card .extra .link {
    font-size: 1.14285714rem;
  }
}
</style>
